import { TableCellsIcon, ArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useChartBuilderStore } from '../../store/chartBuilderStore';
import VisualChartBuilder from '../charts/VisualChartBuilder';

export default function VisualBuilderTab() {
  const {
    visualConfig,
    schema,
    chartType,
    tableName,
    selectedSourceType,
    selectedDataSource,
    wideViews,
    isCustomSQL,
    updateVisualConfig,
  } = useChartBuilderStore();

  const isView = selectedSourceType === 'wideview' || selectedSourceType === 'view';
  const selectedView = isView ? wideViews.find((v) => v._id === selectedDataSource) : null;
  const viewNotDeployed = selectedView && selectedView.deployedToBigQuery === false;

  const handleQueryGenerated = (generatedQuery, config) => {
    if (!isCustomSQL) {
      updateVisualConfig(config, generatedQuery);
    }
  };

  const sourceType =
    selectedSourceType === 'wideview' || selectedSourceType === 'view'
      ? 'wideview'
      : selectedSourceType === 'mart'
        ? 'mart'
        : selectedSourceType === 'datasource'
          ? 'datasource'
          : 'datasource';

  if (!schema) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 px-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-surface-100 dark:bg-surface-800 flex items-center justify-center mb-4">
          <TableCellsIcon className="w-8 h-8 text-surface-400 dark:text-surface-500" />
        </div>
        <h3 className="text-base font-semibold text-surface-800 dark:text-surface-200 mb-2">
          No data source selected
        </h3>
        <p className="text-sm text-surface-500 dark:text-surface-400 max-w-xs mb-4">
          The Visual Builder needs a data source to load your schema and columns. Select one above to get started.
        </p>
        <div className="flex items-center gap-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 animate-bounce">
          <ArrowUpIcon className="w-4 h-4" />
          Choose a data source above
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {viewNotDeployed && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800 dark:text-red-200 mb-1">
                View not deployed to BigQuery
              </h4>
              <p className="text-sm text-red-700 dark:text-red-300">
                This view hasn&apos;t been deployed to BigQuery yet. You can build your query here, but
                the preview will fail until the view is deployed. Go to <strong>Data Studio &rarr; Views</strong> to
                deploy it.
              </p>
            </div>
          </div>
        </div>
      )}
      {isCustomSQL && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-amber-500 text-lg">⚠️</span>
            <div>
              <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                Custom SQL Mode
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                You&apos;re using custom SQL from the SQL Editor. Changes here will regenerate the
                SQL and exit custom SQL mode.
              </p>
            </div>
          </div>
        </div>
      )}
      <VisualChartBuilder
        schema={schema}
        chartType={chartType}
        sourceType={sourceType}
        tableName={tableName}
        onQueryGenerated={handleQueryGenerated}
        initialConfig={visualConfig}
      />
    </div>
  );
}
